import { createRouter, createWebHistory } from "vue-router";
import { useUserStore } from "core-fe-lib/stores/user-store";
import { useTenantStore } from "core-fe-lib/stores/tenant-store";
import { type Role } from "core-fe-lib/openapi/core/models/Role";
import {
  kratosService,
  updateUserFromSession,
} from "core-fe-lib/authentication/vue";

import routes from "./routes";
import { DefaultService } from "core-fe-lib/openapi/core";
import { getModules } from "core-fe-lib/components-shadcn/shell/registry";
import { isModuleEnabledForUser } from "core-fe-lib/components-shadcn/shell/feature-gate";

// Returns the landing path for the first enabled feature module, sorted by landingPriority.
function getFirstFeatureRoute(): string {
  const tenantStore = useTenantStore();
  const userStore = useUserStore();
  const features = tenantStore.tenant?.features;

  const firstModule = getModules()
    .filter((m) => m.landingPath && m.landingPriority !== undefined)
    .filter((m) =>
      isModuleEnabledForUser(
        m,
        features || {},
        !!userStore.isAdmin,
        !!userStore.isSuperAdmin
      )
    )
    .sort((a, b) => (a.landingPriority ?? 99) - (b.landingPriority ?? 99))[0];

  if (firstModule?.landingPath) return firstModule.landingPath;

  // No feature module is enabled for this tenant (e.g. the apex/marketing
  // tenant whose features are empty). A logged-in user must never be dropped
  // back on the public marketing landing ("/"), so fall back to the LMS
  // catalog — the product's default authenticated surface (auth-gated, not
  // feature-gated). Properly-configured tenants never reach this fallback.
  return "/lms";
}

// Single in-flight promise — both tenant + session fetched in parallel, resolved once.
let authPromise: Promise<void> | null = null;

function initializeAuth(): Promise<void> {
  if (authPromise) return authPromise;

  authPromise = (async () => {
    const tenantStore = useTenantStore();

    // Fetch tenant + session in parallel for speed, but DO NOT call
    // updateUserFromSession until the tenant is in the store. That function
    // reads `tenantStore.tenant?.tenant_id` to pick the right entry from the
    // identity's tenant_memberships; if the tenant isn't loaded yet it falls
    // back to global_roles only, which for invitees is empty — and the
    // router guard then bounces them to /no-access even though their Kratos
    // identity has the right role for the current subdomain.
    const tenantFetch = tenantStore.tenant?.tenant_id
      ? Promise.resolve(null)
      : DefaultService.getPublicTenant().catch((err) => {
          console.warn("⚠️  Failed to load tenant in router:", err);
          return null;
        });
    const sessionFetch = kratosService.getSession().catch((err) => {
      console.error("❌ Failed to initialize auth:", err);
      return null;
    });

    const [tenant, session] = await Promise.all([tenantFetch, sessionFetch]);
    if (tenant) tenantStore.setTenant(tenant);
    await updateUserFromSession(session);
  })();

  return authPromise;
}

// Create router instance
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior: () => ({ left: 0, top: 0 }),
  routes,
});

// Navigation guard for authentication
router.beforeEach(async (to, _from, next) => {
  const userStore = useUserStore();

  // Check if route requires authentication
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  // For the home/index route, initialize auth so we can redirect logged-in users.
  if (to.name === "index" || to.path === "/" || to.path === "/home") {
    await initializeAuth();
    if (userStore.isLogged) {
      const featureRoute = getFirstFeatureRoute();
      // Only redirect if the destination is different from the current path
      if (featureRoute !== to.path) {
        return next(featureRoute);
      }
      // If we are already at the destination, proceed to the regular checks (roles, etc.)
    } else {
      return next();
    }
  }

  // The signup route is gated by the tenant's allow_sign_up flag. Load the
  // tenant first, then redirect to signin when public sign-up is disabled —
  // the page-level "disabled" card alone does not stop direct navigation.
  if (to.name === "signup") {
    await initializeAuth();
    const tenantStore = useTenantStore();
    if (!tenantStore.tenant?.allow_sign_up) {
      return next({ name: "signin" });
    }
    return next();
  }

  // For public routes (marketing pages), do not block navigation on auth/session/tenant calls.
  if (!requiresAuth) {
    // Best-effort: if user hits /signin while already logged in, redirect after auth initializes.
    if (to.path === "/signin") {
      void initializeAuth().then(() => {
        const userStore = useUserStore();
        if (userStore.isLogged) {
          void router.replace("/");
        }
      });
    }
    return next();
  }

  // Protected routes must initialize auth state before access checks.
  await initializeAuth();

  // User must be authenticated
  if (userStore.isLogged) {
    // Check if user has any roles (access to this tenant)
    // Skip this check for the no-access page itself
    // Skip when path is /verify/webauthn or /register/webauthn
    if (
      to.name !== "no-access" &&
      to.name !== "verify-webauthn" &&
      to.name !== "register-webauthn" &&
      !to.meta?.skipRoleCheck &&
      (!userStore.user?.roles || userStore.user.roles.length === 0)
    ) {
      console.warn("⚠️  User has no roles for this tenant");
      return next("/no-access");
    }

    // Check for required privilege
    const requiredPrivilege = to.meta?.requiredPrivilege as Role;
    if (requiredPrivilege) {
      if (userStore.hasPrivilege(requiredPrivilege)) {
        return next();
      } else {
        // User does not have privilege, redirect to home
        console.warn(`User lacks required privilege: ${requiredPrivilege}`);
        return next("/");
      }
    }
    return next();
  } else {
    // Not authenticated, redirect to signin
    return next(`/signin?from=${encodeURIComponent(to.fullPath)}`);
  }
});

// Recover from chunk-load failures (Vite dep re-optimization races in dev, stale
// chunks after a deploy in prod). Reload to the target so the fresh module graph
// is fetched, but only ONCE per path — if the reload didn't fix it, the cause is
// not a transient cache issue and we let the error surface instead of looping.
const RELOAD_FLAG = "router:chunk-reload";
router.onError((error, to) => {
  const msg = (error as Error)?.message ?? "";
  const isChunkLoadError =
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Importing a module script failed");
  if (!isChunkLoadError) return;

  if (sessionStorage.getItem(RELOAD_FLAG) === to.fullPath) {
    sessionStorage.removeItem(RELOAD_FLAG);
    console.error(
      "Dynamic import still failing after reload:",
      to.fullPath,
      error
    );
    return;
  }
  sessionStorage.setItem(RELOAD_FLAG, to.fullPath);
  globalThis.location.assign(to.fullPath);
});

// After navigation hook
router.afterEach((to) => {
  // Set document title with base title (subdomain or domain name)
  const baseTitle = globalThis.__baseTitle || "CTO UP";

  if (to.meta.title) {
    document.title = `${to.meta.title} - ${baseTitle}`;
  } else {
    document.title = baseTitle;
  }
});

export default router;
